import * as React from "react";
import { Pencil, Trash2 } from "lucide-react";
import type { IncomeSummary, SourceDocument } from "@/types/tax";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { PdfPreview } from "@/components/domain/PdfPreview";
import { SourceControl } from "@/components/domain/SourceControl";
import { gstFromGross } from "@/lib/gst";
import { currency } from "@/lib/utils";

const providerLabels: Record<IncomeSummary["provider"], string> = {
  uber: "Uber",
  didi: "Didi",
  manual: "Manual",
  other: "Other",
};

export function IncomeTable({
  incomeSummaries,
  documents,
  periodLabel,
  onEdit,
  onDelete,
}: {
  incomeSummaries: IncomeSummary[];
  documents: SourceDocument[];
  periodLabel: string;
  onEdit: (income: IncomeSummary) => void;
  onDelete: (id: string) => void;
}) {
  const [previewIncomeId, setPreviewIncomeId] = React.useState<string | null>(null);
  const documentsById = React.useMemo(() => new Map(documents.map((document) => [document.id, document])), [documents]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Income records</CardTitle>
        <CardDescription>Showing records for {periodLabel}. These monthly records feed G1, 1A, and platform-fee GST credits.</CardDescription>
      </CardHeader>
      <CardContent>
        {incomeSummaries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface p-6 text-center">
            <p className="font-semibold text-ink">No income for {periodLabel}</p>
            <p className="mt-1 text-sm text-muted">Change the reporting period, enter a monthly summary, or scan a Pro PDF draft.</p>
          </div>
        ) : (
          <>
          <div className="grid gap-3 sm:hidden">
            {incomeSummaries.map((income) => {
              const sourceDocument = income.sourceDocumentId ? documentsById.get(income.sourceDocumentId) : null;
              const isPreviewOpen = previewIncomeId === income.id;

              return (
                <div key={income.id} className="rounded-lg border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">{providerLabels[income.provider]}</p>
                      <p className="mt-1 text-sm text-muted">
                        {income.periodStart} to {income.periodEnd}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button type="button" variant="ghost" size="icon" aria-label={`Edit income ${income.periodEnd}`} onClick={() => onEdit(income)}>
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-danger hover:text-danger"
                        aria-label={`Delete income ${income.periodEnd}`}
                        onClick={() => onDelete(income.id)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <MobileMetric label="Income" value={currency(income.grossIncome)} />
                    <MobileMetric label="GST on income" value={currency(gstFromGross(income.grossIncome))} tone="payable" />
                    <MobileMetric label="Platform fees" value={currency(income.platformFeesGross)} />
                    <MobileMetric label="Fee GST credit" value={currency(income.platformFeesGst)} tone="credit" />
                    <div className="col-span-2">
                      <p className="text-xs font-semibold text-muted">Source</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <SourceControl
                          source={income.sourceType === "document_scan" ? "document_scan" : "manual"}
                          document={sourceDocument}
                          expanded={isPreviewOpen}
                          onToggle={sourceDocument?.storagePath ? () => setPreviewIncomeId((current) => (current === income.id ? null : income.id)) : undefined}
                        />
                      </div>
                    </div>
                  </div>
                  {isPreviewOpen && sourceDocument ? (
                    <PdfPreview
                      documentId={sourceDocument.id}
                      fileName={sourceDocument.fileName}
                      className="mt-4"
                      autoOpen
                      onClose={() => setPreviewIncomeId(null)}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left text-sm">
              <thead>
                <tr className="text-xs font-semibold text-muted">
                  <th className="border-b border-border px-3 py-2">Period</th>
                  <th className="border-b border-border px-3 py-2">Provider</th>
                  <th className="border-b border-border px-3 py-2 text-right">Income</th>
                  <th className="border-b border-border px-3 py-2 text-right">GST on income</th>
                  <th className="border-b border-border px-3 py-2 text-right">Platform fees</th>
                  <th className="border-b border-border px-3 py-2 text-right">Fee GST credit</th>
                  <th className="border-b border-border px-3 py-2">Source</th>
                  <th className="border-b border-border px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {incomeSummaries.map((income) => {
                  const sourceDocument = income.sourceDocumentId ? documentsById.get(income.sourceDocumentId) : null;
                  const isPreviewOpen = previewIncomeId === income.id;

                  return (
                    <React.Fragment key={income.id}>
                      <tr className="text-ink hover:bg-surface">
                        <td className="border-b border-border px-3 py-3">
                          {income.periodStart} to {income.periodEnd}
                        </td>
                        <td className="border-b border-border px-3 py-3">{providerLabels[income.provider]}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right">{currency(income.grossIncome)}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right font-semibold text-payable">
                          {currency(gstFromGross(income.grossIncome))}
                        </td>
                        <td className="tabular border-b border-border px-3 py-3 text-right">{currency(income.platformFeesGross)}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right font-semibold text-credit">
                          {currency(income.platformFeesGst)}
                        </td>
                        <td className="border-b border-border px-3 py-3">
                          <div className="flex flex-wrap gap-2">
                            <SourceControl
                              source={income.sourceType === "document_scan" ? "document_scan" : "manual"}
                              document={sourceDocument}
                              expanded={isPreviewOpen}
                              onToggle={sourceDocument?.storagePath ? () => setPreviewIncomeId((current) => (current === income.id ? null : income.id)) : undefined}
                            />
                          </div>
                        </td>
                        <td className="border-b border-border px-3 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button type="button" variant="ghost" size="icon" aria-label={`Edit income ${income.periodEnd}`} onClick={() => onEdit(income)}>
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-danger hover:text-danger"
                              aria-label={`Delete income ${income.periodEnd}`}
                              onClick={() => onDelete(income.id)}
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isPreviewOpen && sourceDocument ? (
                        <tr>
                          <td colSpan={8} className="border-b border-border px-3 py-3">
                            <PdfPreview
                              documentId={sourceDocument.id}
                              fileName={sourceDocument.fileName}
                              autoOpen
                              onClose={() => setPreviewIncomeId(null)}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MobileMetric({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "credit" | "payable" }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className={`tabular mt-1 font-semibold ${tone === "credit" ? "text-credit" : tone === "payable" ? "text-payable" : "text-ink"}`}>{value}</p>
    </div>
  );
}
